// TODO - Type 'any' needs to be fixed.
import {
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  StarSharp,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { GridRowId } from "@mui/x-data-grid";
import { addMinutes, differenceInMinutes, format } from "date-fns";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  ASSIGN_SHIFT,
  FASTEST,
  LOCATION,
  PRIMARYJOB,
  SHIFT_DETAILS,
  SKILLLEVEL,
  WCFIFO,
  WORKCENTER,
} from "../../../../../../assets/constants";
import { FLEX_SCHEDULER_PERMISSIONS } from "../../../../../../config/permissions";
import TimeRangeBar from "../../../../../../design/time-range-bar";
import {
  CustomWindowCard,
  STEP_MINUTES,
} from "../../../../../../design/time-window-stepper";
import {
  formatWorkerLastUpdated,
  hasPermissionString,
  parseSeniority,
} from "../../../../../../utils/helpers";
import MultiLineTooltip from "../../../../../../utils/multi-line-tooltip";
import Highlight from "../../../../../Highlight";
import { APPLIED, ASSIGNED, CONFIRMED } from "../constants";
import ShiftStatus from "../ShiftStatus";

interface ftShift {
  shiftName: string;
  StartDateTime: string;
  EndDateTime: string;
}

interface WorkerAvailabilityCellProps {
  params: any;
  shiftMetadata: any;
  isPartialSignupEnabled: boolean;
  isFlexiSignupEnabled: boolean;
  getAvailableTimeRange: (ftShifts: ftShift[] | null | undefined) => {
    ftAvailableStartTime?: string;
    ftAvailableEndTime?: string;
    availableStartTime?: string;
    availableEndTime?: string;
  };
  activeStep: string;
  workerSlotSelections: { [workerId: string]: any };
  onWorkerSlotChange: (
    workerId: string,
    slot: number,
    customStartTime?: string,
    customEndTime?: string
  ) => void;
  availableSlots: { fullShift: number; firstHalf: number; secondHalf: number };
  isSelected: boolean;
  openFlexiSlots?: {
    start: string;
    end: string;
    isFullSpot?: boolean;
    prevWorkerId?: string;
    nextWorkerId?: string;
  }[];
  workersRequired: number;
}

const WorkerAvailabilityCell: FC<WorkerAvailabilityCellProps> = ({
  params,
  shiftMetadata,
  isPartialSignupEnabled,
  isFlexiSignupEnabled,
  getAvailableTimeRange,
  activeStep,
  workerSlotSelections,
  onWorkerSlotChange,
  availableSlots,
  isSelected,
  openFlexiSlots,
  workersRequired,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [isCustomWindowExpanded, setIsCustomWindowExpanded] =
    React.useState(false);
  const isMenuOpen = Boolean(anchorEl);

  const selectedSlotData = workerSlotSelections[params.row.id];
  const selectedSlot =
    typeof selectedSlotData === "object"
      ? selectedSlotData.slot
      : selectedSlotData ?? params.row.workerShiftSlot ?? -1;

  const customSlotData =
    selectedSlot === 3 &&
    typeof selectedSlotData === "object" &&
    selectedSlotData.customStartTime &&
    selectedSlotData.customEndTime
      ? selectedSlotData
      : null;

  const minFlexiMinutes = (shiftMetadata?.minFlexiSignupHours || 0) * 60;

  const visibleOpenSpots = (openFlexiSlots || [])
    .filter((s) => !s.isFullSpot)
    .filter(
      (s) =>
        String(s.prevWorkerId) !== String(params.row.id) &&
        String(s.nextWorkerId) !== String(params.row.id)
    )
    .filter(
      (s) =>
        differenceInMinutes(new Date(s.end), new Date(s.start)) >=
        minFlexiMinutes
    );

  const matchesOpenSpot =
    !!customSlotData &&
    (openFlexiSlots || []).some((s) => {
      const sStart = new Date(s.start).getTime();
      const sEnd = new Date(s.end).getTime();
      const cStart = new Date(customSlotData.customStartTime).getTime();
      const cEnd = new Date(customSlotData.customEndTime).getTime();
      return (
        Math.abs(sStart - cStart) < 1000 && Math.abs(sEnd - cEnd) < 1000
      );
    });

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsCustomWindowExpanded(selectedSlot === 3 && !matchesOpenSpot);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (
    option: number,
    customStartTime?: string,
    customEndTime?: string
  ) => {
    setIsCustomWindowExpanded(false);
    onWorkerSlotChange(params.row.id, option, customStartTime, customEndTime);
    closeMenu();
  };

  const fullShiftDisabled =
    availableSlots.fullShift <= 0 ||
    (availableSlots.fullShift > 0 && selectedSlot === 0);
  const firstHalfDisabled =
    (availableSlots.firstHalf <= 0 && selectedSlot > 0) ||
    (availableSlots.firstHalf > 0 && selectedSlot === 1);
  const secondHalfDisabled =
    (availableSlots.secondHalf <= 0 && selectedSlot > 0) ||
    (availableSlots.secondHalf > 0 && selectedSlot === 2);

  const stepperBounds = useMemo(() => {
    const shiftStartDate = new Date(shiftMetadata.startDateTime);
    const shiftEndDate = new Date(shiftMetadata.endDateTime);

    let minStart = shiftStartDate;
    let maxEnd = shiftEndDate;

    if (customSlotData) {
      const currentStart = customSlotData.customStartTime;
      const currentEnd = customSlotData.customEndTime;

      const currentStartMs = new Date(currentStart).getTime();
      const currentEndMs = new Date(currentEnd).getTime();

      const leftGap = (openFlexiSlots || []).find(
        (s) =>
          String(s.nextWorkerId) === String(params.row.id) &&
          new Date(s.start).getTime() <= currentStartMs + 1000 &&
          new Date(s.end).getTime() >= currentStartMs - 1000
      );

      if (leftGap) {
        minStart = new Date(leftGap.start);
      } else {
        minStart = new Date(currentStart);
      }

      const rightGap = (openFlexiSlots || []).find(
        (s) =>
          String(s.prevWorkerId) === String(params.row.id) &&
          new Date(s.start).getTime() <= currentEndMs + 1000 &&
          new Date(s.end).getTime() >= currentEndMs - 1000
      );

      if (rightGap) {
        maxEnd = new Date(rightGap.end);
      } else {
        maxEnd = new Date(currentEnd);
      }
    } else if (visibleOpenSpots.length > 0) {
      minStart = new Date(visibleOpenSpots[0].start);
      maxEnd = new Date(visibleOpenSpots[0].end);
    }

    return { minStart, maxEnd };
  }, [
    customSlotData,
    openFlexiSlots,
    visibleOpenSpots,
    shiftMetadata.startDateTime,
    shiftMetadata.endDateTime,
    params.row.id,
  ]);

  const isCustomWindowSelected =
    !!customSlotData && (isCustomWindowExpanded || !matchesOpenSpot);
  const customWindowValue = {
    start: customSlotData ? new Date(customSlotData.customStartTime) : null,
    end: customSlotData ? new Date(customSlotData.customEndTime) : null,
  };

  const isOneWorkerRequired = workersRequired === 1;
  const hasSmallOpenSpot = visibleOpenSpots.some((s) => {
    const duration = differenceInMinutes(new Date(s.end), new Date(s.start));
    return duration < 2 * minFlexiMinutes;
  });

  const shouldHideCustomWindow = false;

  // Default to the first available open spot when a worker is selected and no
  // full/half spot is available (only leftover partial windows remain). The
  // supervisor can still switch to another open spot or a custom window.
  React.useEffect(() => {
    if (
      isSelected &&
      selectedSlot === -1 &&
      visibleOpenSpots.length >= 1 &&
      availableSlots.fullShift === 0 &&
      availableSlots.firstHalf === 0 &&
      availableSlots.secondHalf === 0
    ) {
      const firstSpot = visibleOpenSpots[0];
      onWorkerSlotChange(
        params.row.id,
        3,
        firstSpot.start,
        firstSpot.end
      );
    }
  }, [
    isSelected,
    availableSlots,
    visibleOpenSpots,
    selectedSlot,
    params.row.id,
    onWorkerSlotChange,
  ]);

  const handleSelectCustomWindow = () => {
    setIsCustomWindowExpanded(true);

    if (selectedSlot === 3) return;

    // Seed with the first open spot if any are available. Otherwise,
    // seed the "maximum" window (full shift minus 30 mins) by default.
    if (visibleOpenSpots.length > 0) {
      const firstSpot = visibleOpenSpots[0];
      onWorkerSlotChange(params.row.id, 3, firstSpot.start, firstSpot.end);
    } else {
      const seedEnd = addMinutes(
        new Date(shiftMetadata.endDateTime),
        -STEP_MINUTES
      );
      onWorkerSlotChange(
        params.row.id,
        3,
        shiftMetadata.startDateTime,
        seedEnd.toISOString()
      );
    }
  };

  const handleCustomWindowChange = (start: Date, end: Date) => {
    // Commit instantly on every accepted step; keep the popover open.
    onWorkerSlotChange(
      params.row.id,
      3,
      start.toISOString(),
      end.toISOString()
    );
  };

  let availableStartTime: string;
  let availableEndTime: string;

  const halfBoxStyle = {
    flex: 1,
    p: "8px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    width: "50%",
  };

  if (
    (isPartialSignupEnabled || isFlexiSignupEnabled) &&
    params.row.workerShiftStartDateTime &&
    params.row.workerShiftEndDateTime
  ) {
    availableStartTime = params.row.workerShiftStartDateTime;
    availableEndTime = params.row.workerShiftEndDateTime;
  } else {
    const { ftAvailableStartTime, ftAvailableEndTime } = getAvailableTimeRange(
      params.row.ftShifts
    );
    availableStartTime = ftAvailableStartTime ?? shiftMetadata.startDateTime;
    availableEndTime = ftAvailableEndTime ?? shiftMetadata.endDateTime;
  }

  if (
    activeStep !== ASSIGN_SHIFT ||
    !isSelected ||
    (!isPartialSignupEnabled && !isFlexiSignupEnabled)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        <Box sx={{ width: "125px" }}>
          <TimeRangeBar
            totalStartTime={shiftMetadata.startDateTime}
            totalEndTime={shiftMetadata.endDateTime}
            availableStartTime={
              availableStartTime ?? shiftMetadata.startDateTime
            }
            availableEndTime={availableEndTime ?? shiftMetadata.endDateTime}
          />
        </Box>
      </Box>
    );
  }

  if (activeStep === ASSIGN_SHIFT) {
    switch (selectedSlot) {
      case 1: // First Half
        availableEndTime = new Date(
          (new Date(shiftMetadata.startDateTime).getTime() +
            new Date(shiftMetadata.endDateTime).getTime()) /
            2
        ).toISOString();
        break;
      case 2: // Second Half
        availableStartTime = new Date(
          (new Date(shiftMetadata.startDateTime).getTime() +
            new Date(shiftMetadata.endDateTime).getTime()) /
            2
        ).toISOString();
        break;
      case 3: // Flexi
        if (
          typeof selectedSlotData === "object" &&
          selectedSlotData.customStartTime &&
          selectedSlotData.customEndTime
        ) {
          availableStartTime = selectedSlotData.customStartTime;
          availableEndTime = selectedSlotData.customEndTime;
        } else {
          availableStartTime =
            params.row.workerShiftStartDateTime || shiftMetadata.startDateTime;
          availableEndTime =
            params.row.workerShiftEndDateTime || shiftMetadata.endDateTime;
        }
        break;
      default: // Full Shift (0)
        break;
    }
  }

  return (
    // Fill the cell like the read-only bar does, so both centre on the column
    <Box sx={{ width: "100%" }}>
      <Box
        onClick={openMenu}
        sx={{
          cursor: "pointer",
          display: "flex",
          flexDirection: "row",
          gap: "4px",
          alignItems: "center",
          justifyContent: "center",
          // Matches the arrow (24px) plus the gap, keeping the bar centred
          paddingLeft: "28px",
        }}
      >
        {/* Same width as the read-only bar, so ticked rows line up */}
        <Box sx={{ width: "125px" }}>
          <TimeRangeBar
            totalStartTime={shiftMetadata.startDateTime}
            totalEndTime={shiftMetadata.endDateTime}
            availableStartTime={availableStartTime}
            availableEndTime={availableEndTime}
          />
        </Box>
        {isMenuOpen ? (
          <KeyboardArrowUp
            sx={{ fontSize: "16px", color: "rgba(0,0,0,0.87)" }}
          />
        ) : (
          <KeyboardArrowDown
            sx={{ fontSize: "16px", color: "rgba(0,0,0,0.87)" }}
          />
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <MenuItem
          disableRipple
          sx={{ p: 1, "&:hover": { backgroundColor: "transparent" } }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              minWidth: "380px",
            }}
          >
            {(!isFlexiSignupEnabled || availableSlots.fullShift > 0) && (
              <Box
                onClick={() => !fullShiftDisabled && handleOptionClick(0)}
                sx={{
                  p: "8px 12px",
                  borderRadius: "4px",
                  cursor: fullShiftDisabled ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  backgroundColor:
                    selectedSlot === 0 ? "rgba(25, 118, 210, 0.12)" : "#f5f5f5",
                  "&:hover": {
                    backgroundColor: !fullShiftDisabled
                      ? selectedSlot === 0
                        ? "rgba(25, 118, 210, 0.25)"
                        : "#e0e0e0"
                      : undefined,
                  },
                }}
              >
                <StarSharp
                  sx={{
                    fontSize: "20px",
                    color: !fullShiftDisabled
                      ? selectedSlot === 0
                        ? "#1976d2"
                        : "rgba(0, 0, 0, 0.87)"
                      : "rgba(0, 0, 0, 0.38)",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: selectedSlot === 0 ? 500 : 400,
                    color: !fullShiftDisabled
                      ? selectedSlot === 0
                        ? "#1976d2"
                        : "rgba(0, 0, 0, 0.87)"
                      : "rgba(0, 0, 0, 0.38)",
                  }}
                >
                  {availableSlots.fullShift !== 1
                    ? t("scheduler_full-shift-spot-plural", {
                        n: availableSlots.fullShift,
                      })
                    : t("scheduler_full-shift-spot-singular", {
                        n: availableSlots.fullShift,
                      })}
                </Typography>
              </Box>
            )}
            {!isFlexiSignupEnabled && (
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                }}
              >
                <Box
                  onClick={() => !firstHalfDisabled && handleOptionClick(1)}
                  sx={{
                    ...halfBoxStyle,
                    cursor: firstHalfDisabled ? "not-allowed" : "pointer",
                    backgroundColor:
                      selectedSlot === 1
                        ? "rgba(25, 118, 210, 0.12)"
                        : "#f5f5f5",
                    "&:hover": {
                      backgroundColor: !firstHalfDisabled
                        ? selectedSlot === 1
                          ? "rgba(25, 118, 210, 0.25)"
                          : "#e0e0e0"
                        : undefined,
                    },
                  }}
                >
                  <StarHalfIcon
                    sx={{
                      fontSize: "20px",
                      color: !firstHalfDisabled
                        ? selectedSlot === 1
                          ? "#1976d2"
                          : "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.38)",
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: selectedSlot === 1 ? 500 : 400,
                      color: !firstHalfDisabled
                        ? selectedSlot === 1
                          ? "#1976d2"
                          : "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.38)",
                    }}
                  >
                    {availableSlots.firstHalf !== 1
                      ? t("scheduler_first-half-spot-plural", {
                          n: availableSlots.firstHalf,
                        })
                      : t("scheduler_first-half-spot-singular", {
                          n: availableSlots.firstHalf,
                        })}
                  </Typography>
                </Box>
                <Box
                  onClick={() => !secondHalfDisabled && handleOptionClick(2)}
                  sx={{
                    ...halfBoxStyle,
                    cursor: secondHalfDisabled ? "not-allowed" : "pointer",
                    backgroundColor:
                      selectedSlot === 2
                        ? "rgba(25, 118, 210, 0.12)"
                        : "#f5f5f5",
                    "&:hover": {
                      backgroundColor: !secondHalfDisabled
                        ? selectedSlot === 2
                          ? "rgba(25, 118, 210, 0.25)"
                          : "#e0e0e0"
                        : undefined,
                    },
                  }}
                >
                  <StarHalfIcon
                    sx={{
                      transform: "scaleX(-1)",
                      fontSize: "20px",
                      color: !secondHalfDisabled
                        ? selectedSlot === 2
                          ? "#1976d2"
                          : "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.38)",
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: selectedSlot === 2 ? 500 : 400,
                      color: !secondHalfDisabled
                        ? selectedSlot === 2
                          ? "#1976d2"
                          : "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.38)",
                    }}
                  >
                    {availableSlots.secondHalf !== 1
                      ? t("scheduler_second-half-spot-plural", {
                          n: availableSlots.secondHalf,
                        })
                      : t("scheduler_second-half-spot-singular", {
                          n: availableSlots.secondHalf,
                        })}
                  </Typography>
                </Box>
              </Box>
            )}
            {isFlexiSignupEnabled && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {visibleOpenSpots.length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                        mt: 1,
                      }}
                    >
                      {t("scheduler_available-open-spots")}
                    </Typography>
                    {visibleOpenSpots.map((slot, index) => {
                      const isSelected =
                        selectedSlot === 3 &&
                        selectedSlotData?.customStartTime === slot.start &&
                        selectedSlotData?.customEndTime === slot.end;
                      return (
                        <Box
                          key={index}
                          onClick={() => {
                            setIsCustomWindowExpanded(false);
                            handleOptionClick(3, slot.start, slot.end);
                          }}
                          sx={{
                            p: "8px 12px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: isSelected
                              ? "rgba(25, 118, 210, 0.12)"
                              : "#f5f5f5",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "rgba(25, 118, 210, 0.25)"
                                : "#e0e0e0",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: isSelected ? 500 : 400,
                              color: isSelected
                                ? "#1976d2"
                                : "rgba(0, 0, 0, 0.87)",
                            }}
                          >
                            {visibleOpenSpots.length === 1
                              ? t("scheduler_open-spot")
                              : t("scheduler_spot-number", {
                                  number: index + 1,
                                })}{" "}
                            (
                            {format(new Date(slot.start), "h:mm a")} -{" "}
                            {format(new Date(slot.end), "h:mm a")})
                          </Typography>
                        </Box>
                      );
                    })}
                  </>
                )}
                {!shouldHideCustomWindow && (
                  <CustomWindowCard
                    selected={isCustomWindowSelected}
                    shiftStart={stepperBounds.minStart}
                    shiftEnd={stepperBounds.maxEnd}
                    minMinutes={minFlexiMinutes}
                    maxMinutes={
                      differenceInMinutes(
                        new Date(shiftMetadata.endDateTime),
                        new Date(shiftMetadata.startDateTime)
                      ) - STEP_MINUTES
                    }
                    value={customWindowValue}
                    onSelect={handleSelectCustomWindow}
                    onChange={handleCustomWindowChange}
                    headerLabel={t("flexi_shift_custom_window")}
                    hintLabel={t("flexi_custom_step_hint")}
                    startLabel={t("flexi_shift_start_time")}
                    endLabel={t("flexi_shift_end_time")}
                  />
                )}
              </Box>
            )}
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const useShiftDetailsDialogColumns = (
  shiftMetadata: any,
  numberOfWorkers: number,
  workerAvailableForAssign: number,
  search: string,
  activeStep: string,
  shiftAllocationType: string,
  isOpenForOtherLocation: boolean,
  isWorkerSubTypeEnabled: boolean,
  isOverlappigSignupEnabled: boolean,
  isPartialSignupEnabled: boolean,
  isFlexiSignupEnabled: boolean,
  overlappingSignupLimitMins: number,
  workers: any,
  handleRemoveWorker: any,
  totalFirstHalfSpotsConfirmed: number,
  totalSecondHalfSpotsConfirmed: number,
  totalFullSpotsConfirmed: number,
  workersRequired: number,
  workerSlotSelections: { [workerId: string]: any },
  onWorkerSlotChange: (
    workerId: string,
    slot: number,
    customStartTime?: string,
    customEndTime?: string
  ) => void,
  availableSlots: { fullShift: number; firstHalf: number; secondHalf: number },
  workersSelectedForAssignShift: GridRowId[],
  openFlexiSlots?: {
    start: string;
    end: string;
    isFullSpot?: boolean;
    prevWorkerId?: string;
    nextWorkerId?: string;
  }[],
  activeTab?: number
) => {
  const { t } = useTranslation();
  const permissions = useSelector(
    (state: any) => state.user.userData.permissions
  );

  const getAvailableTimeRange = useCallback(
    (ftShifts: ftShift[] | null | undefined) => {
      if (!Array.isArray(ftShifts) || ftShifts.length === 0) {
        return {
          availableStartTime: new Date(
            shiftMetadata.startDateTime
          ).toISOString(),
          availableEndTime: new Date(shiftMetadata.endDateTime).toISOString(),
        };
      }

      let availableStartTime = new Date(shiftMetadata.startDateTime);
      let availableEndTime = new Date(shiftMetadata.endDateTime);

      ftShifts.forEach((ftShift) => {
        if (!ftShift?.StartDateTime || !ftShift?.EndDateTime) {
          return;
        }

        const ftStart = new Date(ftShift.StartDateTime);
        const ftEnd = new Date(ftShift.EndDateTime);

        // Adjust available time based on overlaps
        if (ftEnd > availableStartTime && ftEnd < availableEndTime) {
          if (
            differenceInMinutes(ftEnd, availableStartTime) <=
            overlappingSignupLimitMins
          ) {
            availableStartTime = ftEnd;
          }
        }
        if (ftStart > availableStartTime && ftStart < availableEndTime) {
          if (
            differenceInMinutes(availableEndTime, ftStart) <=
            Number(overlappingSignupLimitMins)
          ) {
            availableEndTime = ftStart;
          }
        }
      });

      return {
        ftAvailableStartTime: availableStartTime.toISOString(),
        ftAvailableEndTime: availableEndTime.toISOString(),
      };
    },
    [shiftMetadata, overlappingSignupLimitMins]
  );

  const allColumns = useMemo(() => {
    try {
      const workersColumn = {
        field: "workerName",
        headerName: t("scheduler_worker-name"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">{t("scheduler_worker-name-count", {
              n:
                activeStep === ASSIGN_SHIFT
                  ? workerAvailableForAssign
                  : numberOfWorkers,
            })}</Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            {params.row.errorMessage !== "" && activeStep === ASSIGN_SHIFT && (
              <Tooltip placement="right" title={params.row.errorMessage}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    marginRight: "8px",
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: "#E44B48",
                      width: "18px",
                      height: "18px",
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={params.row.workerName}>
              <Typography
                variant="subtitle2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                }}
              >
                <Highlight value={params.row.workerName} searchText={search} />
              </Typography>
            </Tooltip>
          </Box>
        ),
        minWidth: 180,
        flex: 1,
        checked: true,
        label: t("scheduler_worker-name"),
      };

      const workerTypeColumn = {
        field: "workerTypeText",
        headerName: t("scheduler_worker-type"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">{t("scheduler_worker-type")}</Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <Box>
            <Typography
              sx={{
                flexWrap: "wrap",
                wordBreak: "break-word",
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {params.row.workerTypeText}
            </Typography>
          </Box>
        ),
        minWidth: 100,
        flex: 1,
        checked: true,
        label: t("scheduler_worker-type"),
      };

      const workerSubTypeColumn = {
        field: "workerSubTypeText",
        headerName: t("scheduler_worker-sub-type"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">{t("scheduler_worker-sub-type")}</Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <Box>
            <Typography
              sx={{
                flexWrap: "wrap",
                wordBreak: "break-word",
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {params.row.workerSubType || "-"}
            </Typography>
          </Box>
        ),
        minWidth: 100,
        flex: 1,
        checked: false,
        label: t("scheduler_worker-sub-type"),
        valueGetter: (params: any) => params.row.workerSubType || "-",
      };

      const workerStatusColumn = {
        field: "workerStatus",
        headerName: t("scheduler_worker-status"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">{t("scheduler_worker-status")}</Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <Box>
            <Typography
              sx={{
                flexWrap: "wrap",
                wordBreak: "break-word",
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {params.row.workerStatusName}
            </Typography>
          </Box>
        ),
        minWidth: 120,
        flex: 1,
        checked: false,
        label: t("scheduler_worker-status"),
      };

      const locationsColumn = {
        field: "location",
        headerName: t("scheduler_location"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">{t("scheduler_location")}</Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <Box>
            <Typography
              sx={{
                flexWrap: "wrap",
                wordBreak: "break-word",
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              {params.row.location}
            </Typography>
          </Box>
        ),
        minWidth: 130,
        flex: 1,
        checked:
          isOpenForOtherLocation &&
          (shiftAllocationType === LOCATION || shiftAllocationType === FASTEST),
        label: t("scheduler_location"),
      };

      const shiftTimeColumn = {
        field: "fullTimeShifts",
        headerName: t("scheduler_shift-pattern-schedule"),
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box>
            <Typography variant="subtitle2">
              {t("scheduler_shift-pattern-schedule")}
            </Typography>
          </Box>
        ),
        renderCell: (params: any) => (
          <MultiLineTooltip
            text={params?.row?.fullTimeShifts ?? "-"}
            searchText={search}
          />
        ),
        minWidth: 190,
        flex: 1,
        checked:
          (shiftAllocationType === FASTEST &&
            workers?.some(
              (worker: any) => worker?.workerType === "FullTime"
            )) ||
          shiftAllocationType === WORKCENTER ||
          shiftAllocationType === WCFIFO,
        label: t("scheduler_shift-pattern-schedule"),
        valueGetter: (params: any) => params?.row?.fullTimeShifts ?? "-",
      };

      const seniorityColumn = {
        field: "seniorityYears",
        headerName: t("scheduler_seniority"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_seniority")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <Box pl={1}>{params.row.seniorityYears}</Box>;
        },
        valueGetter: (params: any) =>
          parseSeniority(params.row.seniorityYears ?? ""),
        minWidth: 190,
        flex: 1,
        checked: shiftAllocationType !== FASTEST,
        label: t("scheduler_seniority"),
      };

      const reportingManager = {
        field: "reportingManager",
        headerName: t("scheduler_reporting-manager"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_reporting-manager")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          let reportingManager = params.row.reportingManager ?? "-";
          if (reportingManager === "") {
            reportingManager = "-";
          }
          return (
            <Box
              pl={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                <Highlight value={reportingManager} searchText={search} />
              </Typography>
            </Box>
          );
        },
        valueGetter: (params: any) => params.row.reportingManager ?? "",
        minWidth: 180,
        flex: 1,
        checked: false,
        label: t("scheduler_reporting-manager"),
      };
      const statusColumn = {
        field: "Status",
        headerName: t("scheduler_status"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_status")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <ShiftStatus
                label={params?.row?.status}
                reason={params?.row?.reason}
              />

              {params.row.status === "Removed" && (
                <Tooltip placement="right" title={params.row.removeReason}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        color: "#0288D1",
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        },
        valueGetter: (params: any) => params.row.status,
        minWidth: 150,
        flex: 1,
        checked: true,
        label: t("scheduler_status"),
      };

      const overtimeColumn = {
        field: "overtime",
        headerName: t("scheduler_ytd-overtime-hours"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_ytd-overtime-hours")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <Box pl={1}>{params.row?.overtime}</Box>;
        },
        valueGetter: (params: any) => params.row?.overtimeMins,
        minWidth: 160,
        flex: 1,
        checked:
          shiftAllocationType === PRIMARYJOB ||
          shiftAllocationType === WORKCENTER ||
          shiftAllocationType === WCFIFO ||
          shiftAllocationType === SKILLLEVEL,
        label: t("scheduler_ytd-overtime-hours"),
      };

      const workCenterColumn = {
        field: "workCenter",
        headerName: t("scheduler_work-center"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_work-center")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <Box pl={1}>{params.row?.workCenter?.name ?? "-"}</Box>;
        },
        valueGetter: (params: any) => params.row?.workCenter?.name,
        minWidth: 160,
        flex: 1,
        checked: true,
        label: t("scheduler_work-center"),
      };

      const primaryJobNameColumn = {
        field: "primaryJobName",
        headerName: t("scheduler_primary-job"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  // paddingLeft: "8px",
                }}
              >
                {t("scheduler_primary-job")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <MultiLineTooltip text={params?.row?.primaryJobName ?? "-"} />;
        },
        valueGetter: (params: any) => params.row?.primaryJobName,
        minWidth: 160,
        flex: 1,
        checked: shiftAllocationType === PRIMARYJOB,
        label: t("scheduler_primary-job"),
      };

      const badgeNumberColumn = {
        field: "badgeNumber",
        headerName: t("scheduler_badge-number"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_badge-number")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <Box pl={1}>{params?.row?.badgeNumber ?? "-"}</Box>;
        },
        valueGetter: (params: any) => params?.row?.badgeNumber ?? "-",
        minWidth: 160,
        flex: 1,
        checked: shiftAllocationType === PRIMARYJOB,
        label: t("scheduler_badge-number"),
      };

      const updatedAtColumn = {
        field: "lastUpdatedUtc",
        headerName: t("scheduler_updated-at"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_updated-at")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return (
            <Box pl={1}>
              {formatWorkerLastUpdated(params?.row?.lastUpdatedUtc) || "-"}
            </Box>
          );
        },
        valueGetter: (params: any) =>
          formatWorkerLastUpdated(params?.row?.lastUpdatedUtc) || "-",
        minWidth: 160,
        flex: 1,
        checked: true,
        label: t("scheduler_updated-at"),
      };

      const workerIdColumn = {
        field: "companyEmployeeId",
        headerName: t("scheduler_worker-id"),
        disableColumnMenu: true,
        renderHeader: () => {
          return (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  paddingLeft: "8px",
                }}
              >
                {t("scheduler_worker-id")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          return <Box pl={1}>{params?.row?.companyEmployeeId}</Box>;
        },
        valueGetter: (params: any) => params?.row?.companyEmployeeId,
        minWidth: 160,
        flex: 1,
        checked: false,
        label: t("scheduler_worker-id"),
        sortComparator: (v1: any, v2: any) => {
          const getNumericPart = (
            value: string | undefined | null
          ): number | null => {
            if (!value) return null;
            const trimmedValue = value.trim(); // Trim spaces
            const match = trimmedValue.match(/^\d+$/); // Match fully numeric values
            return match ? parseInt(trimmedValue, 10) : null;
          };

          const isBlank = (value: string | undefined | null) =>
            value === null || value === undefined || value.trim() === "";

          // Handle blank/null/undefined values
          const blank1 = isBlank(v1);
          const blank2 = isBlank(v2);

          if (blank1 && blank2) return 0; // Both are blank
          if (blank1) return 1; // Blank values should come last
          if (blank2) return -1; // Blank values should come first

          const num1 = getNumericPart(v1);
          const num2 = getNumericPart(v2);

          // First compare by numerical value
          if (num1 !== null && num2 !== null) {
            return num1 - num2;
          }

          // If one is numeric and the other is not, prioritize the numeric one
          if (num1 !== null) return -1;
          if (num2 !== null) return 1;

          // Fall back to standard string comparison
          return v1.localeCompare(v2);
        },
      };

      const workerAvailabilityColumn = {
        field: "workerAvailability",
        headerName: t("scheduler_worker-hours"),
        disableColumnMenu: true,
        sortable: false,
        renderHeader: () => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.87)",
                  // paddingLeft: "8px",
                }}
              >
                {t("scheduler_worker-hours")}
              </Typography>
            </Box>
          );
        },
        renderCell: (params: any) => {
          const isSelected = workersSelectedForAssignShift.includes(
            params.row.id
          );
          return (
            <WorkerAvailabilityCell
              params={params}
              shiftMetadata={shiftMetadata}
              isPartialSignupEnabled={isPartialSignupEnabled}
              isFlexiSignupEnabled={isFlexiSignupEnabled}
              getAvailableTimeRange={getAvailableTimeRange}
              activeStep={activeStep}
              workerSlotSelections={workerSlotSelections}
              onWorkerSlotChange={onWorkerSlotChange}
              availableSlots={availableSlots}
              isSelected={isSelected}
              openFlexiSlots={openFlexiSlots}
              workersRequired={workersRequired}
            />
          );
        },
        minWidth: 180,
        width: 180,
        flex: 1,
        checked: true,
        label: t("scheduler_worker-hours"),
      };

      const actionColumn = {
        field: "actions",
        // headerName left as "Actions" identifier: filtered by
        // `headerName !== "Actions"` in the parent dialog; header cell renders empty.
        headerName: "Actions",
        headerAlign: "right",
        align: "right",
        minWidth: 100,
        width: 100,
        cellClassName: "hover-column-cell",
        headerClassName: "hover-column-header",
        sortable: false,
        checked: true,
        disableColumnMenu: true,
        filterable: false,
        renderHeader: () => (
          <span
            style={{
              fontWeight: "bold",
              width: "74px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {""}
          </span>
        ),
        renderCell: (params: any) => {
          if (
            hasPermissionString(
              permissions,
              FLEX_SCHEDULER_PERMISSIONS.REMOVE_WORKER
            ) &&
            (params?.row?.status === CONFIRMED ||
              params?.row?.status === ASSIGNED ||
              params?.row?.status === APPLIED)
          ) {
            return (
              <Box
                className="hover-content"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "120px",
                  padding: "6px 0px 2px 0px",
                  borderRadius: "4px",
                  zIndex: 1,
                }}
              >
                <Box width="80px">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={(e) => handleRemoveWorker(params?.row)}
                    sx={{ width: "80px" }}
                  >
                    {t("scheduler_remove")}
                  </Button>
                </Box>
              </Box>
            );
          } else {
            return <></>;
          }
        },
      };
      return [
        workersColumn,
        statusColumn,
        ...(isOverlappigSignupEnabled ||
        isPartialSignupEnabled ||
        isFlexiSignupEnabled
          ? [workerAvailabilityColumn]
          : []),
        workerTypeColumn,
        ...(isWorkerSubTypeEnabled ? [workerSubTypeColumn] : []),
        workerStatusColumn,
        workCenterColumn,
        locationsColumn,
        shiftTimeColumn,
        overtimeColumn,
        seniorityColumn,
        reportingManager,
        primaryJobNameColumn,
        ...(shiftAllocationType === PRIMARYJOB ? [badgeNumberColumn] : []),
        workerIdColumn,
        ...(activeStep === SHIFT_DETAILS && activeTab === 0
          ? [updatedAtColumn]
          : []),
        actionColumn,
      ];
    } catch (e) {
      console.log("Error setting up column headers", e);
      return [];
    }
  }, [
    isOpenForOtherLocation,
    shiftAllocationType,
    workers,
    isOverlappigSignupEnabled,
    isPartialSignupEnabled,
    isFlexiSignupEnabled,
    isWorkerSubTypeEnabled,
    activeStep,
    workerAvailableForAssign,
    numberOfWorkers,
    search,
    workersSelectedForAssignShift,
    shiftMetadata,
    getAvailableTimeRange,
    workerSlotSelections,
    onWorkerSlotChange,
    availableSlots,
    permissions,
    handleRemoveWorker,
    openFlexiSlots,
    workersRequired,
    activeTab,
    t,
  ]);

  return {
    allColumns,
  };
};

export default useShiftDetailsDialogColumns;
